import RestoreIcon from "@mui/icons-material/Restore";
import ButtonBase from "@mui/material/ButtonBase";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { Fragment, useContext, useMemo, type MouseEvent, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { DialogContext } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import Loader from "_shared/_views/loading/loader";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { getCommentsByProposal } from "work/proposals/comments/_store/comments-redux-slice";
import type Comment from "work/proposals/comments/comment";
import {
  proposalChangeActorLabel,
  proposalCreationBaselineLabel,
  proposalReviewerAccessLabel,
} from "work/proposals/negotiation/proposal-change-description";
import {
  buildMergedProposalTimelineView,
  buildProposalTimelineView,
  proposalTimelineCommentMessageId,
  type ProposalTimelineComment,
  type ProposalTimelineEntryView,
  type ProposalTimelineReviewerChange,
  type ProposalTimelineTeamChange,
} from "work/proposals/negotiation/proposal-timeline";
import { useGetProposalTimelineQuery } from "work/proposals/negotiation/proposal-collaboration-api";
import type { ProposalTimelineEntry } from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  selectEffectiveProposalJSON,
  selectProposalCollaborationEntry,
  selectProposalCollaborationViewerScope,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import type {
  ProposalChangeDescriptionContext,
  ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import { buildProposalPendingChangePreview } from "work/proposals/negotiation/proposal-pending-change-preview";
import ProposalChangeSummaryList from "work/proposals/_views/_shared/proposal-change-summary-list";
import {
  buildChangeLineItemLabelIndex,
  buildItemLabelIndex,
  buildItemValueIndex,
  useProposalUserNameResolver,
} from "work/proposals/_views/_shared/use-proposal-change-description-context";
import { proposalFeeScheduleTeamMemberUserIds } from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import {
  isProposalActivityEntry,
  type ProposalActivityCoverage,
} from "work/proposals/negotiation/proposal-activity";
import {
  proposalFieldRestoreCandidates,
  proposalFieldUnsavedValue,
} from "work/proposals/negotiation/proposal-field-restore";
import { proposalTimelineQueryPathForField } from "work/proposals/negotiation/proposal-field-timeline-path";
import {
  ProposalChangeAddedValue,
  ProposalChangeRemovedValue,
} from "work/proposals/negotiation/proposal-change-value-redline";
import ProposalFeeScheduleChangeRedline from "work/proposals/negotiation/proposal-fee-schedule-change-redline";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

const TimelineList = styled(List)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.5),
  paddingBottom: 0,
  paddingTop: 0,
}));

// A colored rule down the left edge, in the change's own color, rather than the raw path/JSON audit
// rows the stream carries.
const TimelineEntry = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "colorToken",
})<{ colorToken: ProposalTimelineEntryView["colorToken"] }>(({ theme, colorToken }) => ({
  borderLeft: `3px solid ${timelineColor(colorToken)}`,
  display: "block",
  marginBottom: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.75),
  paddingLeft: theme.spacing(1.25),
  paddingTop: theme.spacing(0.75),
}));

/**
 * The unsaved half of the stream: the same blue rule as a recorded entry, dashed, because what it
 * marks has not happened yet. It sits above the history rather than inside it — these rows have no
 * sequence, no author attribution from the server and no timestamp, and the record must stay the
 * record.
 */
const PendingPreview = styled("div")(({ theme }) => ({
  borderLeft: `3px dashed ${timelineColor("blue")}`,
  marginBottom: theme.spacing(1),
  paddingBottom: theme.spacing(0.25),
  paddingLeft: theme.spacing(1.25),
}));

// Author avatar column beside each entry's text.
const EntryLayout = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  gap: theme.spacing(1),
}));
const EntryContent = styled("div")(() => ({
  flex: 1,
  minWidth: 0,
}));
const AvatarButton = styled(ButtonBase)(({ theme }) => ({
  borderRadius: "50%",
  flexShrink: 0,
  marginTop: theme.spacing(0.25),
}));
const AvatarSlot = styled("div")(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(0.25),
}));
const EntryAvatar = styled(IndividualAvatar)(({ theme }) => ({
  height: theme.spacing(3.5),
  width: theme.spacing(3.5),
}));
const IndividualRefContainer = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
  // An inline-flex box sizes itself from its content, so without these a name wider than the column
  // simply overhangs it — the avatar holds the left edge and the name runs off the right.
  maxWidth: "100%",
  overflowWrap: "anywhere",
  verticalAlign: "middle",
}));
const IndividualRefButton = styled(ButtonBase)(() => ({
  borderRadius: 4,
  verticalAlign: "middle",
}));

/**
 * A team or reviewer change reads as two lines: what happened, then who it happened to (#5243).
 *
 * It used to be one sentence — "Added Reviewer: {avatar}{name} (Comment only)" — with the avatar and
 * the name set into the middle of it as a single `inline-flex` run. That run cannot break, and these
 * entries are only ever read in a column 320px to 440px wide, so the line wrapped around it rather
 * than inside it: the person landed hard against the label in front of them and the rights in
 * brackets behind them, and a name of any length pushed the brackets onto a line by themselves.
 *
 * Splitting at the colon puts the avatar at the start of a line, where it has the label above it
 * rather than a word beside it, and gives the name the column's full width to wrap in. The detail
 * stays with the person it describes — the rights belong to them, not to the fact that a change
 * happened.
 */
const ChangeHeadlineLabel = styled("span")(() => ({
  display: "block",
}));
const ChangeHeadlineSubject = styled("span")(({ theme }) => ({
  display: "block",
  // A quarter-step, so the label does not read as crowded by the 28px avatar below it. The pair stays
  // a pair: what separates them from the timestamp is `EntryTimestamp`, which every entry shares.
  marginTop: theme.spacing(0.5),
}));

/**
 * When an entry happened, under what it says happened.
 *
 * The half-step is the column's one rule about this, rather than each kind of entry answering for
 * itself. It arrived because a reviewer change puts a 28px avatar directly above the timestamp and
 * the two were touching — but a timestamp is a different fact from the entry above it everywhere, not
 * only where the entry is tall, and giving only the tall ones air left them looking unlike their
 * neighbours.
 */
const EntryTimestamp = styled(Typography)(({ theme }) => ({
  // `caption` maps to a <span>, which is what this has to be: the slot around it is a <div> holding
  // parsed entry bodies, and a <p> here would bring default margins of its own into the rule above.
  display: "block",
  marginTop: theme.spacing(1),
}));

/**
 * An earlier value the viewer may put back (#6874): it takes the pointer and the focus, as anything
 * that does something on a click must, and carries the same restore icon the revert menu's rows do.
 */
const RestoreValueButton = styled(ButtonBase)(({ theme }) => ({
  alignItems: "center",
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  display: "inline-flex",
  gap: theme.spacing(0.5),
  maxWidth: "100%",
  textAlign: "left",
  "&:hover, &.Mui-focusVisible": {
    backgroundColor: theme.palette.action.hover,
  },
}));

/** Stands at the foot of the list, the oldest end, which is where the viewer's history begins. */
const HistoryBoundaryNote = styled(Typography)(({ theme }) => ({
  display: "block",
  paddingTop: theme.spacing(1),
  textAlign: "center",
}));

// The change-value redline lives in its own module so the submit/tranche dialogs draw a change exactly
// the way this timeline does — see proposal-change-value-redline.tsx.
const RemovedValue = ProposalChangeRemovedValue;
/** Stable empty list: two memos below take `visibleComments` as a dependency. */
const NO_COMMENTS: Comment[] = [];
/** Stable empty list for the same reason, on the branch where no preview is drawn at all. */
const NO_PENDING_CHANGES: ProposalChangeDescriptor[] = [];
/** Names the preview for what it is wherever it is drawn — the pane, and each field's own popover. */
export const proposalPendingChangePreviewHeading = "Not saved yet";
const AddedValue = ProposalChangeAddedValue;

type ProposalTimelinePanelProps = {
  negotiationId?: string;
  viewerScope?: string;
  field?: ProposalField;
  /**
   * Narrows the history to one item of a collection field — one fee-schedule category, one team
   * member — by the item's canonical identity in the negotiation stream (#4951).
   *
   * A collection item's `ProposalField` carries an id, but its timeline path is still its collection's
   * (`/feeSchedule`; see `proposalTimelinePathForField`), so a category row's history used to show every
   * category's events, and the row-level control read as a copy of the whole-schedule one. With this
   * set, the entries of every other item are left out, and the fee schedule's grouping still folds the
   * remaining leaves into one row per save. Explicit rather than derived from `field.id` because an
   * item's stream identity is not always that id: a team member's is the compound
   * `userId:legalEntityId` (see `ProposalFieldTimelineDropdown.collectionItemId`).
   */
  collectionItemId?: string;
  /** The popover asks for nothing while it is closed; skip the query otherwise. */
  enabled?: boolean;
  /** The Changes tab shows only content changes; the All tab keeps comments interleaved. */
  includeComments?: boolean;
  /**
   * Renders a comment entry as the comment itself rather than as a line of history about it.
   *
   * A host listing the conversation through this panel supplies it so the negotiation's comments stay
   * MANAGEABLE where they are read: an author edits, retracts, deletes or re-aims their own comment on
   * the row it appears on, and a recipient marks one unread there. Surfaces that only report what
   * happened leave it out and get the plain descriptive row. None draws comments here since #5398 — the
   * Comments pane lists them itself.
   */
  renderComment?: (comment: Comment) => ReactNode;
  /**
   * Which negotiation this history is, relative to the page the reader is on.
   *
   * Absent for a proposal that continues no earlier negotiation. On a revision the field's popover
   * draws two of these panels: "revision", this negotiation's own changes, and "beforeRevision", the
   * negotiation the revision continues (#5567). An empty "revision" list says "no changes in this
   * revision" rather than "no changes yet", so the older entries beneath it are not read as current
   * ones; and the earlier list is named for itself, so it is never counted as one of this
   * negotiation's changes.
   */
  period?: ProposalTimelinePeriod;
  /**
   * Shows the negotiation's activity instead of one field's history: the entries that no field control
   * on the page reads (#5648). See `proposal-activity.ts`. Takes the place of `field`.
   *
   * Without it the panel draws one field's history, and a panel with no field, or with a field that has
   * no timeline path, draws nothing. It never asks for the whole negotiation on a field's behalf.
   */
  activity?: ProposalActivityCoverage;
  /**
   * Puts an earlier value of this field back. Supplied only where the viewer may edit the field, and
   * only for a plain scalar field, whose history entries each hold one value (#6874). Each earlier
   * value then draws as a button that hands its entry here; the field's current value does not, since
   * restoring it would change nothing. Without it the history reads and does nothing on a click.
   */
  onRestoreValue?: (entry: ProposalTimelineEntryView) => void;
};

/** See {@link ProposalTimelinePanelProps.period}. */
export type ProposalTimelinePeriod = "revision" | "beforeRevision";

/** What an empty history says: always, and for a reader whose history starts where they joined (#5246). */
const emptyTextByPeriod: Record<ProposalTimelinePeriod | "none", { always: string; sinceJoined: string }> = {
  none: {
    always: "No visible changes yet.",
    sinceJoined: "No visible changes since you joined this proposal.",
  },
  revision: {
    always: "No changes in this revision.",
    sinceJoined: "No visible changes in this revision since you joined it.",
  },
  beforeRevision: {
    always: "No changes before this revision.",
    sinceJoined: "No visible changes before this revision since you joined it.",
  },
};

/** The same, for the negotiation's activity rather than a field's changes. */
const activityEmptyTextByPeriod: Record<ProposalTimelinePeriod | "none", { always: string; sinceJoined: string }> = {
  none: {
    always: "No activity yet.",
    sinceJoined: "No activity since you joined this proposal.",
  },
  revision: {
    always: "No activity in this revision.",
    sinceJoined: "No activity in this revision since you joined it.",
  },
  beforeRevision: {
    always: "No activity before this revision.",
    sinceJoined: "No activity before this revision since you joined it.",
  },
};

function toTimelineComment(comment: Comment): ProposalTimelineComment {
  return {
    messageId: comment.id?.value ?? "",
    // A comment belongs to the negotiation, not to a field: a proposal has one conversation, so there is
    // no field path to attribute it to.
    path: null,
    // Messaging comments don't carry the Work negotiation party/legal-entity attribution; author
    // identity is resolved from the sender's user id.
    sourceParty: null,
    authorUserId: comment.senderId?.value ?? null,
    authorLegalEntityId: null,
    // A comment the author has just posted carries no moment at all until the server answers, and this
    // list is ordered by moment. Dating it from the epoch put it at the very foot of the pane — below
    // every change and every other comment — for as long as the write was in flight, and then moved it
    // to the top when the answer landed (#5125). It is the newest thing in the negotiation, so it is
    // dated now, which is where it will stay once the server stamps it. `CommentItem` dates the row it
    // draws the same way.
    createdDate: comment.enteredConversationAt?.toISOString() ?? new Date().toISOString(),
    publishedDate: comment.published?.toISOString() ?? null,
    content: comment.content,
  };
}

export default function ProposalTimelinePanel(props: Readonly<ProposalTimelinePanelProps>) {
  const { negotiationId, viewerScope, collectionItemId, period, renderComment, activity, onRestoreValue } = props;
  // The activity view is about no one field: it previews nothing and asks for no field's path.
  const field = activity ? undefined : props.field;
  const enabled = props.enabled ?? true;
  const includeComments = props.includeComments ?? true;

  // A structured scalar with a path of its own is read from that path. The team leader is the one:
  // `ProposalField.TeamLeader(id)` carries an id for comment scoping, but its events live at
  // `/vendorTeam/leader` (see `ProposalField.timelineIdentity`), and reading the team's whole thread
  // instead gave the leader's history control every member's comings and goings (#4954) — the same
  // list the roster's own control showed, and the revert menu beside it never did.
  const path = proposalTimelineQueryPathForField(field);
  // A field with no path has no history of its own. Without a path the endpoint answers with the whole
  // negotiation, so only the activity view, which filters that answer below, may ask without one (#5648).
  const readsSomething = Boolean(activity || path);
  const canQuery = Boolean(enabled && negotiationId && viewerScope && readsSomething);
  // The item is asked for on the wire rather than sieved out of the collection's whole thread once it
  // arrives (#5118). Fetching every category's history to show one was right while the collection was
  // the finest thing the server could answer about, but it is the schedule's history being read and
  // then mostly thrown away — which costs the most on exactly the schedules with the most to read, and
  // would quietly start losing entries the day the timeline is paginated or truncated.
  const timelineQuery = useGetProposalTimelineQuery(
    canQuery
      ? { negotiationId: negotiationId!, viewerScope: viewerScope!, path, itemId: collectionItemId }
      : skipToken,
  );

  // The "All" view merges the Messaging comments into the Work field timeline. Comments are
  // Messaging-native (a Message in a topic-filtered Forum keyed by the durable negotiationId), so they
  // come from the Forum/Message comment store — the same store the `messages` Ably middleware feeds,
  // which is what keeps this view live. The drawer's default Comments tab hydrates that store, so the
  // panel only reads it (and reflects realtime pushes) without triggering its own fetch.
  const negotiationGuid = useMemo(() => {
    if (!negotiationId) return undefined;
    try {
      return new Guid(negotiationId);
    } catch {
      return undefined;
    }
  }, [negotiationId]);

  const allComments = useSelector(getCommentsByProposal(negotiationGuid));

  // Every comment on the negotiation, unfiltered by field: the conversation is proposal-wide, so scoping
  // it to the field the panel is showing would hide the whole of it whenever a field was named.
  //
  // Unpublished comments belong here too. The store only ever holds what the server served this actor,
  // and an author-only draft is served to nobody but its author — so an unpublished comment in the store
  // is the reader's own, and dropping it stranded it: a comment they had just retracted disappeared from
  // the only surface that could publish or delete it again.
  //
  // A withheld comment belongs here too, as the tombstone the server serves in its place (#4564): the
  // row keeps its place in the thread saying "This comment was deleted" — or, for one its author took
  // back, "Removed at ..." — carries no body, and CommentItem draws it. Filtering it out here made that
  // branch unreachable. Who may see a tombstone is already decided server-side: one is hydrated only
  // where `Published != null || SenderId == reader`, so a comment deleted while it was still an
  // author-only draft reaches its author and nobody else, and a retraction is a tombstone to the
  // audience and the whole comment to its author.
  const visibleComments = allComments ?? NO_COMMENTS;

  const comments = useMemo<ProposalTimelineComment[]>(
    () => (includeComments ? visibleComments.map(toTimelineComment) : []),
    [includeComments, visibleComments],
  );

  const commentsByMessageId = useMemo(
    () => new Map(visibleComments.filter((comment) => comment.id).map((comment) => [comment.id!.value, comment])),
    [visibleComments],
  );

  // Names the collection item each entry is about. Most items name themselves from the value the entry
  // carries, but a document cannot: its file name is display metadata the ledger strips, so a policy or
  // conflicts document produced a row that named its collection and nothing else — three documents on
  // one proposal rendering as three identical "added Client Policies". The projection still holds every
  // name, and the panel already reads this store for the comments it merges. The baseline (the server's
  // own projection) is indexed first and the effective one second, so an item a pending removal has
  // taken out of the effective projection is still nameable while a rename still reads as the new name.
  const collaborationEntry = useSelector(selectProposalCollaborationEntry(negotiationId ?? ""));
  const effectiveProjection = useSelector(selectEffectiveProposalJSON(negotiationId ?? ""));
  const resolveItemLabel = useMemo(() => {
    // The change lines come first and are overwritten by both projections, because they name the one
    // item no projection can: the one a SAVED removal took out of them. A saved document replacement
    // is exactly that — an add carrying `replacesId` and a removal — so without this the history's
    // removal entry read as a bare "Conflicts Document" (#5549).
    const labels = buildChangeLineItemLabelIndex(collaborationEntry?.envelope?.changeLines);
    for (const [key, label] of buildItemLabelIndex(collaborationEntry?.envelope?.projection)) {
      labels.set(key, label);
    }
    for (const [key, label] of buildItemLabelIndex(effectiveProjection)) labels.set(key, label);
    return (itemPath: string, itemId: string) =>
      labels.get(`${itemPath}|${itemId.toLowerCase()}`) ?? labels.get(itemId.toLowerCase());
  }, [collaborationEntry?.envelope?.changeLines, collaborationEntry?.envelope?.projection, effectiveProjection]);

  // The same lookup at whole-item granularity. A fee-schedule row draws the entire category with only
  // the columns that moved blue-lined (#4845); the columns that did not move are the state those events
  // happened to, so they come from the projection and are folded back through the history from there.
  //
  // The SERVER'S projection alone, unlike the label index above. This value is the seed of a backwards
  // fold: `resolveFeeScheduleCategoryStates` takes it as the state after the newest event in
  // `timelineEntries` and undoes each event to reach every older row. The effective projection is that
  // state plus the viewer's unsaved local operations — edits no timeline entry accounts for — so
  // seeding with it leaves them in place all the way down, and an unsaved billing code appears as
  // historical context on rate rows from weeks ago. History is what was persisted; pending work belongs
  // to the editor, not to the record.
  const resolveItemValue = useMemo(() => {
    const values = buildItemValueIndex(collaborationEntry?.envelope?.projection);
    return (itemPath: string, itemId: string) =>
      values.get(`${itemPath}|${itemId.toLowerCase()}`) ?? values.get(itemId.toLowerCase());
  }, [collaborationEntry?.envelope?.projection]);

  // The same lookup again, this time preferring the EFFECTIVE projection — the one exception to the
  // rule above, and for the same reason it is a rule. A pending fee-schedule edit is not history; it is
  // the editor's own work, and drawing it as the whole category it belongs to means reading that
  // category as the editor currently has it. This index feeds only the unsaved preview below, never the
  // recorded entries.
  const resolvePendingItemValue = useMemo(() => {
    const values = buildItemValueIndex(collaborationEntry?.envelope?.projection);
    for (const [key, value] of buildItemValueIndex(effectiveProjection)) values.set(key, value);
    return (itemPath: string, itemId: string) =>
      values.get(`${itemPath}|${itemId.toLowerCase()}`) ?? values.get(itemId.toLowerCase());
  }, [collaborationEntry?.envelope?.projection, effectiveProjection]);

  const timelineEntries = useMemo(() => {
    const entries = scopeTimelineEntriesToItem(timelineQuery.data?.entries, collectionItemId);
    return activity && entries ? entries.filter((entry) => isProposalActivityEntry(entry, activity)) : entries;
  }, [activity, collectionItemId, timelineQuery.data?.entries]);
  // The one column of a fee-schedule category whose value is a person — its assigned member — reads as
  // that person's name (#4949). Names come from the store, and the people are asked for below with the
  // entry authors, so the row settles on the name once the lookup lands.
  const resolveUserName = useProposalUserNameResolver();

  // What the next Save will write, drawn before it is asked for (#5133). The local queue belongs to
  // whichever scope the store has active, so a pane rendered for another one previews nothing rather
  // than attributing one actor's unsaved work to another's view of the negotiation.
  const activeViewerScope = useSelector(selectProposalCollaborationViewerScope);
  const pendingChangeContext = useMemo<ProposalChangeDescriptionContext>(
    () => ({ resolveItemLabel, resolveItemValue: resolvePendingItemValue, resolveUserName }),
    [resolveItemLabel, resolvePendingItemValue, resolveUserName],
  );
  const previewsPendingChanges = Boolean(
    enabled && viewerScope && activeViewerScope === viewerScope && field && readsSomething,
  );
  const pendingChangeDescriptors = useMemo(
    () =>
      previewsPendingChanges
        ? buildProposalPendingChangePreview({
            operations: collaborationEntry?.pendingOperations,
            reviewerChanges: collaborationEntry?.pendingReviewerChanges,
            inFlightCommands: collaborationEntry?.inFlightCommands,
            field,
            context: pendingChangeContext,
          })
        : NO_PENDING_CHANGES,
    [
      collaborationEntry?.inFlightCommands,
      collaborationEntry?.pendingOperations,
      collaborationEntry?.pendingReviewerChanges,
      field,
      pendingChangeContext,
      previewsPendingChanges,
    ],
  );
  const entries = useMemo(
    () =>
      includeComments
        ? buildMergedProposalTimelineView(timelineEntries ?? [], comments, resolveItemLabel, resolveItemValue, resolveUserName)
        : buildProposalTimelineView(timelineEntries ?? [], resolveItemLabel, resolveItemValue, resolveUserName),
    [includeComments, timelineEntries, comments, resolveItemLabel, resolveItemValue, resolveUserName],
  );
  // What the field holds in the viewer's unsaved work, read from the same queue the preview above
  // reads and under the same scope: the timeline has none of it yet (#7085).
  const unsavedValue = useMemo(() => {
    if (!previewsPendingChanges) return undefined;
    const inFlightOperations = Object.values(collaborationEntry?.inFlightCommands ?? {}).flatMap(
      (command) => command?.operations ?? [],
    );
    return proposalFieldUnsavedValue([...inFlightOperations, ...(collaborationEntry?.pendingOperations ?? [])], field);
  }, [collaborationEntry?.inFlightCommands, collaborationEntry?.pendingOperations, field, previewsPendingChanges]);
  // The entries whose value a click puts back: none unless the host may restore, and never the current
  // value, saved or not (see `proposalFieldRestoreCandidates`).
  const restorableEntryKeys = useMemo(
    () =>
      new Set(
        onRestoreValue && field
          ? proposalFieldRestoreCandidates(entries.filter((entry) => !entry.isComment), unsavedValue).map(
              (entry) => entry.key,
            )
          : [],
      ),
    [entries, field, onRestoreValue, unsavedValue],
  );
  // Newest changes read best at the top of the history list.
  const orderedEntries = useMemo(
    () => entries.filter((entry) => includeComments || !entry.isComment).slice().reverse(),
    [entries, includeComments],
  );

  // One lookup for every user named in the list: entry authors (avatar + name), the team member a
  // team-roster change is about (E12 renders "{member} added to team"), the reviewer a reviewer
  // change is about, and the member a fee-schedule category was assigned to or taken from. The last
  // is read off the raw entries: the schedule's leaves are folded into one row per save, and only the
  // row survives the fold.
  const referencedUserIds = useMemo(
    () =>
      new Set([
        ...entries.flatMap((entry) => [
          ...(entry.actorRole !== "System" && entry.actorUserId ? [entry.actorUserId] : []),
          ...(entry.teamChange?.previousUserId ? [entry.teamChange.previousUserId] : []),
          ...(entry.teamChange?.nextUserId ? [entry.teamChange.nextUserId] : []),
          ...(entry.reviewerChange?.reviewerUserId ? [entry.reviewerChange.reviewerUserId] : []),
        ]),
        ...(timelineEntries ?? []).flatMap(proposalFeeScheduleTeamMemberUserIds),
      ]),
    [entries, timelineEntries],
  );
  useGetIndividualsByUserIdsQuery({ userIds: [...referencedUserIds] }, { skip: referencedUserIds.size === 0 });

  if (!negotiationId || !viewerScope || !readsSomething) return null;

  // Only while there is nothing to show. A background refetch — one fires after every save, and after
  // every realtime notice — would otherwise blank the whole stream to a spinner, taking the negotiation's
  // comments off screen with it and putting the row an author was working on out of reach mid-interaction.
  const isLoading = (timelineQuery.isLoading || timelineQuery.isFetching) && orderedEntries.length === 0;

  // A reviewer's history starts where they were let in (#5246), so an empty one is not "nothing ever
  // happened here" — two colleagues may have cleared and refilled this very field the day before. The
  // server withholds what came before, deliberately: it can name documents taken off the proposal before
  // this viewer had any grant to them. What it does say is where the withheld part ends, so the history
  // says it starts there rather than reading as a field nothing has ever happened to.
  const historyStartsWhenJoined = typeof timelineQuery.data?.historyVisibleAfterSequence === "number";
  const emptyTexts = (activity ? activityEmptyTextByPeriod : emptyTextByPeriod)[period ?? "none"];
  const emptyText = historyStartsWhenJoined ? emptyTexts.sinceJoined : emptyTexts.always;
  // A line of body copy: the popover this is drawn in is a ~300px card beside one input, and a first
  // receipt has no history on any field, so this is the common sight there rather than the rare one.
  const emptyMessage = (
    <Typography color="text.secondary" variant="body2">
      {emptyText}
    </Typography>
  );

  const previewsAnything = pendingChangeDescriptors.length > 0;
  // A preview standing where the history is empty is not an empty pane: saying "No visible changes yet"
  // beneath a list of the changes about to be made contradicts it.
  const historyBody = isLoading ? <Loader /> : previewsAnything ? null : emptyMessage;
  // Wherever the empty message is not already saying it. A list with entries in it is the partial case:
  // the oldest change a reviewer let in between a clear and a refill sees is the refill, and without this
  // it reads as the field's whole story.
  const showsBoundaryNote = historyStartsWhenJoined && !isLoading && (orderedEntries.length > 0 || previewsAnything);

  function renderEntry(entry: ProposalTimelineEntryView) {
    // A comment the host knows how to render is rendered as itself — one list row, not a row
    // wrapping a row, so the stream stays a flat list of entries however each one is drawn.
    const comment = commentsByMessageId.get(proposalTimelineCommentMessageId(entry) ?? "");
    const renderedComment = comment && renderComment ? renderComment(comment) : undefined;
    if (renderedComment) return <Fragment key={entry.key}>{renderedComment}</Fragment>;

    const moment = new Date(entry.createdDate);
    const onRestore = onRestoreValue && restorableEntryKeys.has(entry.key) ? () => onRestoreValue(entry) : undefined;
    return (
      <TimelineEntry key={entry.key} colorToken={entry.colorToken} disableGutters>
        <EntryLayout>
          <TimelineEntryAvatar entry={entry} />
          <EntryContent>
            <ListItemText
              slotProps={{ primary: { component: "div" }, secondary: { component: "div" } }}
              primary={<TimelineEntryHeadline entry={entry} />}
              secondary={
                <>
                  <TimelineEntryBody entry={entry} onRestore={onRestore} />
                  <EntryTimestamp variant="caption" color="text.secondary">
                    {moment.toLocaleString()}
                  </EntryTimestamp>
                </>
              }
            />
          </EntryContent>
        </EntryLayout>
      </TimelineEntry>
    );
  }

  const historyList = (
    <TimelineList dense data-testid="proposal-timeline-history-entries">
      {orderedEntries.map((entry) => renderEntry(entry))}
    </TimelineList>
  );

  return (
    // The earlier negotiation's history is named for itself and carries no id: two panels share the
    // popover on a revision, and the page's "Proposal change history" is the revision's own.
    <section aria-label={sectionLabel(period, Boolean(activity))} id={period === "beforeRevision" || activity ? undefined : "proposal-timeline-history"}>
      {previewsAnything && (
        <PendingPreview data-testid="proposal-pending-change-preview">
          {/* The Save confirmation's own list, drawn ahead of the save rather than during it: these are
              the entries this queue will become, in the words the history will use for them. */}
          <ProposalChangeSummaryList
            heading={proposalPendingChangePreviewHeading}
            descriptors={pendingChangeDescriptors}
          />
        </PendingPreview>
      )}
      {isLoading || orderedEntries.length === 0 ? historyBody : historyList}
      {showsBoundaryNote && (
        <HistoryBoundaryNote variant="caption" color="text.secondary">
          Changes shown from when you joined this proposal.
        </HistoryBoundaryNote>
      )}
    </section>
  );
}

function sectionLabel(period: ProposalTimelinePeriod | undefined, isActivity: boolean): string {
  if (isActivity) return period === "beforeRevision" ? "Proposal activity before this revision" : "Proposal activity";
  return period === "beforeRevision" ? "Change history before this revision" : "Proposal change history";
}

/**
 * The entries about one collection item, when the panel is scoped to one — see
 * {@link ProposalTimelinePanelProps.collectionItemId}. Item ids are compared case-insensitively, as the
 * label and value indexes already do: the stream and the projection have not always agreed on casing.
 *
 * Kept now that the item is asked for on the wire, because the two do not overlap in the one window
 * that matters. A server that predates the `itemId` parameter ignores it and answers with the whole
 * collection — which is the point of tolerating unknown input rather than rejecting it, and is what
 * makes a rolling deploy safe. Without this the browser would draw every sibling's history into one
 * category's popover for the length of that window.
 */
function scopeTimelineEntriesToItem(
  entries: readonly ProposalTimelineEntry[] | undefined,
  collectionItemId: string | undefined,
): readonly ProposalTimelineEntry[] | undefined {
  if (!entries || !collectionItemId) return entries;
  const itemId = collectionItemId.toLowerCase();
  return entries.filter((entry) => entry.itemId?.toLowerCase() === itemId);
}

/** Actor avatar; clicking opens that user's profile dialog when a DialogProvider is available. */
function TimelineEntryAvatar({ entry }: Readonly<{ entry: ProposalTimelineEntryView }>) {
  const dialog = useContext(DialogContext);
  const actorUserId = entry.actorRole === "System" ? undefined : entry.actorUserId;
  const authorGuid = useTimelineUserGuid(actorUserId);
  const individual = useSelector(getIndividualByUserId(authorGuid)) ?? undefined;
  // DialogContext defaults to `undefined` when no DialogProvider is mounted (e.g. the field-history
  // popover or a test harness); fall back to a non-interactive avatar there rather than throwing.
  const canOpenProfile = Boolean(authorGuid) && typeof dialog?.openDialog === "function";

  function openProfile() {
    if (!authorGuid || typeof dialog?.openDialog !== "function") return;
    dialog.openDialog({
      component: <ViewIndividualProfile userId={authorGuid} />,
      contentSxProps: { display: "flex" },
      MuiProps: { fullWidth: true, maxWidth: "lg" },
      titleStyle: { paddingBottom: 0 },
      onClose: dialog.closeDialog,
    });
  }

  if (entry.actorRole === "System") return null;
  if (!canOpenProfile) {
    return (
      <AvatarSlot>
        <EntryAvatar individual={individual} size="small" />
      </AvatarSlot>
    );
  }

  return (
    <AvatarButton onClick={openProfile} aria-label="View author profile">
      <EntryAvatar individual={individual} size="small" />
    </AvatarButton>
  );
}

function TimelineEntryHeadline({ entry }: Readonly<{ entry: ProposalTimelineEntryView }>) {
  const authorName = useIndividualFullName(entry.actorRole === "System" ? undefined : entry.actorUserId);

  if (entry.isCreationBaseline) {
    const baselineField = entry.fieldLabel ? ` for ${entry.fieldLabel}` : "";
    const baselineItem = entry.itemLabel ? `: ${entry.itemLabel}` : "";
    return (
      <Typography component="span" variant="body2">
        <TimelineName>{proposalCreationBaselineLabel}</TimelineName>
        {`${baselineField}${baselineItem}`}
      </Typography>
    );
  }

  // Team-roster and team-leader changes read descriptively — "Added Team Member: X", "Updated Team
  // Leader: old → new" — with each individual named, avatared and linked to their profile, rather than a
  // generic "{author} updated Team". The actor is still conveyed by the avatar beside the row.
  if (entry.isTeamChange && entry.teamChange) {
    return (
      <Typography component="span" variant="body2">
        <TeamChangeHeadline change={entry.teamChange} />
      </Typography>
    );
  }

  // A reviewer change names the reviewer and the rights they ended up with — "Added Reviewer: Jane Doe
  // (Full edit)", "Updated Reviewer: Jane Doe (Comment only → Full edit)" — the same words the save
  // dialog uses for the pending change. Every one of them otherwise read "{author} changed reviewer
  // access Reviewers", naming neither the person it was about nor what changed for them.
  if (entry.isReviewerChange && entry.reviewerChange) {
    return (
      <Typography component="span" variant="body2">
        <ReviewerChangeHeadline change={entry.reviewerChange} />
      </Typography>
    );
  }

  // A collection change names the item it is about: three fee-schedule categories added in one save
  // otherwise read as three identical "added Fee Schedule" rows, with nothing to distinguish them and
  // no way to tell which category any of them meant.
  const fieldSuffix = entry.fieldLabel ? ` ${entry.fieldLabel}` : "";
  const suffix = entry.itemLabel ? `${fieldSuffix}: ${entry.itemLabel}` : fieldSuffix;
  return (
    <Typography component="span" variant="body2">
      <TimelineName>
        {proposalChangeActorLabel({
          actorRole: entry.actorRole,
          resolvedName: authorName,
          sourceParty: entry.sourceParty,
        })}
      </TimelineName>
      {` ${entry.actionVerb}${suffix}`}
    </Typography>
  );
}

const teamChangeLabels: Record<ProposalTimelineTeamChange["kind"], string> = {
  "leader-set": "Set Team Leader",
  "leader-cleared": "Removed Team Leader",
  "leader-changed": "Updated Team Leader",
  "member-added": "Added Team Member",
  "member-removed": "Removed Team Member",
  "member-updated": "Updated Team Member",
};

function TeamChangeHeadline({ change }: Readonly<{ change: ProposalTimelineTeamChange }>) {
  // Removed/previous individuals render struck-through; added/incoming ones in blue; both link to the
  // referenced profile. A leader change shows old → new.
  return (
    <>
      <ChangeHeadlineLabel>{`${teamChangeLabels[change.kind]}:`}</ChangeHeadlineLabel>
      <ChangeHeadlineSubject>
        {change.previousUserId && (
          <TimelineIndividualRef userId={change.previousUserId} tone="removed" fallbackName={teamMemberFallbackName} />
        )}
        {change.previousUserId && change.nextUserId ? " " : null}
        {change.nextUserId && (
          <TimelineIndividualRef
            userId={change.nextUserId}
            tone={change.kind === "member-updated" ? "plain" : "added"}
            fallbackName={teamMemberFallbackName}
          />
        )}
        {!change.previousUserId && !change.nextUserId && <TimelineName>{teamMemberFallbackName}</TimelineName>}
      </ChangeHeadlineSubject>
    </>
  );
}

const reviewerChangeLabels: Record<ProposalTimelineReviewerChange["kind"], string> = {
  "reviewer-added": "Added Reviewer",
  "reviewer-removed": "Removed Reviewer",
  "access-changed": "Updated Reviewer",
  "message-changed": "Updated Reviewer",
};

const teamMemberFallbackName = "a team member";
const reviewerFallbackName = "a reviewer";

function ReviewerChangeHeadline({ change }: Readonly<{ change: ProposalTimelineReviewerChange }>) {
  return (
    <>
      <ChangeHeadlineLabel>{`${reviewerChangeLabels[change.kind]}:`}</ChangeHeadlineLabel>
      <ChangeHeadlineSubject>
        {change.reviewerUserId ? (
          <TimelineIndividualRef
            userId={change.reviewerUserId}
            tone={reviewerChangeTone(change.kind)}
            fallbackName={reviewerFallbackName}
          />
        ) : (
          <TimelineName>{reviewerFallbackName}</TimelineName>
        )}
        <ReviewerChangeDetail change={change} />
      </ChangeHeadlineSubject>
    </>
  );
}

function reviewerChangeTone(
  kind: ProposalTimelineReviewerChange["kind"],
): "added" | "removed" | "plain" {
  if (kind === "reviewer-added") return "added";
  if (kind === "reviewer-removed") return "removed";
  return "plain";
}

/**
 * What the change did to the reviewer, trailing their name: the rights an assignment gave them, the
 * rights an access change moved them between (redlined old → new, exactly as a value change is drawn),
 * or whether a message change set or cleared their custom message.
 */
function ReviewerChangeDetail({ change }: Readonly<{ change: ProposalTimelineReviewerChange }>) {
  if (change.kind === "access-changed") {
    if (change.previousCanEdit === undefined && change.canEdit === undefined) return null;
    return (
      <>
        {" ("}
        {change.previousCanEdit !== undefined && (
          <RemovedValue>{proposalReviewerAccessLabel(change.previousCanEdit)}</RemovedValue>
        )}
        {change.canEdit !== undefined && <AddedValue>{proposalReviewerAccessLabel(change.canEdit)}</AddedValue>}
        {")"}
      </>
    );
  }
  if (change.kind === "message-changed") {
    return change.messageCleared ? " (Message cleared)" : " (Message updated)";
  }
  if (change.canEdit === undefined) return null;
  return ` (${proposalReviewerAccessLabel(change.canEdit)})`;
}

/** A named individual in a team or reviewer change: small avatar + name (struck-through / blue by tone)
 *  linking to their profile when a DialogProvider is available. */
function TimelineIndividualRef({
  userId,
  tone,
  fallbackName,
}: Readonly<{ userId: string; tone: "added" | "removed" | "plain"; fallbackName: string }>) {
  const dialog = useContext(DialogContext);
  const guid = useTimelineUserGuid(userId);
  const individual = useSelector(getIndividualByUserId(guid)) ?? undefined;
  const name = individual?.fullName?.trim() || fallbackName;
  const canOpenProfile = Boolean(guid) && typeof dialog?.openDialog === "function";

  const nameNode =
    tone === "removed" ? <RemovedValue>{name}</RemovedValue> : tone === "added" ? <AddedValue>{name}</AddedValue> : <span>{name}</span>;
  const content = (
    <IndividualRefContainer>
      {individual && <EntryAvatar individual={individual} size="small" />}
      {nameNode}
    </IndividualRefContainer>
  );

  if (!canOpenProfile) return content;

  function openProfile(event: MouseEvent<HTMLElement>) {
    event.stopPropagation();
    if (!guid || typeof dialog?.openDialog !== "function") return;
    dialog.openDialog({
      component: <ViewIndividualProfile userId={guid} />,
      contentSxProps: { display: "flex" },
      MuiProps: { fullWidth: true, maxWidth: "lg" },
      titleStyle: { paddingBottom: 0 },
      onClose: dialog.closeDialog,
    });
  }

  return (
    <IndividualRefButton onClick={openProfile} aria-label={`View ${name}'s profile`}>
      {content}
    </IndividualRefButton>
  );
}

function TimelineName({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  );
}

function useTimelineUserGuid(userId?: string | null): Guid | undefined {
  return useMemo(() => {
    if (!userId) return undefined;
    try {
      return new Guid(userId);
    } catch {
      return undefined;
    }
  }, [userId]);
}

function useIndividualFullName(userId?: string | null): string | undefined {
  const guid = useTimelineUserGuid(userId);
  return useSelector(getIndividualByUserId(guid))?.fullName;
}

function TimelineEntryBody({
  entry,
  onRestore,
}: Readonly<{ entry: ProposalTimelineEntryView; onRestore?: () => void }>) {
  if (entry.isComment) {
    return entry.valueSummary ? (
      <Typography component="span" variant="body2" sx={{ display: "block", whiteSpace: "pre-wrap" }}>
        {entry.valueSummary}
      </Typography>
    ) : null;
  }

  // A published alternative carries values like any content change — the baseline the recipient last saw
  // and the target being proposed (`ProposalNegotiationResolutionTargetPublishedPayload`) — and it is the
  // entry where the value matters most: without it the row reads "{party} published an alternative Start
  // Date" and never says what the alternative is. Gating on `isContentChange` alone dropped exactly that
  // value, so a counterparty's first counter to a field went into the history unreadable.
  // A fee-schedule row is a whole save's worth of edits to the schedule, drawn as the schedule: one
  // line per category, the aspects the actor touched redlined in place. Checked before the generic
  // paths because it is the only entry that describes a collection rather than a value.
  if (entry.feeScheduleChange) {
    return <ProposalFeeScheduleChangeRedline summary={entry.feeScheduleChange} />;
  }
  if (!entry.isContentChange && !entry.isPublishedAlternative) return null;
  if (entry.changeDetails?.length) {
    return (
      <div>
        {entry.changeDetails.map((detail) => (
          <Typography key={detail.fieldLabel} component="div" variant="body2">
            <TimelineName>{`${detail.fieldLabel}: `}</TimelineName>
            {detail.previousDisplayValue !== undefined && <RemovedValue>{detail.previousDisplayValue}</RemovedValue>}
            {detail.nextDisplayValue !== undefined && <AddedValue>{detail.nextDisplayValue}</AddedValue>}
          </Typography>
        ))}
      </div>
    );
  }
  if (entry.previousDisplayValue === undefined && entry.nextDisplayValue === undefined) return null;

  const nextValue = entry.nextDisplayValue !== undefined && <AddedValue>{entry.nextDisplayValue}</AddedValue>;
  return (
    <Typography component="span" variant="body2" sx={{ display: "block" }}>
      {entry.previousDisplayValue !== undefined && <RemovedValue>{entry.previousDisplayValue}</RemovedValue>}
      {onRestore ? (
        <RestoreValueButton aria-label={`Restore ${entry.nextDisplayValue}`} title="Restore this value" onClick={onRestore}>
          {nextValue}
          <RestoreIcon fontSize="small" color="action" />
        </RestoreValueButton>
      ) : (
        nextValue
      )}
    </Typography>
  );
}

function timelineColor(colorToken: ProposalTimelineEntryView["colorToken"]): string {
  switch (colorToken) {
    case "blue":
      return "#1976d2";
    case "orange":
      return "#ed6c02";
    case "green":
      return "#2e7d32";
    case "purple":
      return "#7b1fa2";
    case "teal":
      return "#00897b";
    case "pink":
      return "#c2185b";
  }
}
